import React from 'react';
import type { PlatformItem } from '../../utils/gatewayHelpers';

interface PlatformBreakdownProps {
  title: string;
  totalCount: number;
  items: PlatformItem[];
  variant: 'online' | 'offline';
  statusFilter: 'all' | 'active' | 'offline';
  platformFilter: string;
  onPlatformClick: (platform: string) => void;
}

const THEME = {
  online: {
    wrapperBg: 'rgba(34, 197, 94, 0.08)',
    wrapperBorder: '1px solid rgba(34, 197, 94, 0.25)',
    badgeBg: 'rgba(34, 197, 94, 0.2)',
    badgeText: '#86efac',
    btnBg: 'rgba(34, 197, 94, 0.12)',
    btnBorder: '1px solid rgba(34, 197, 94, 0.25)',
    btnSelectedBg: 'rgba(34, 197, 94, 0.28)',
    btnSelectedBorder: '2px solid rgba(34, 197, 94, 0.8)',
    selectedText: '#86efac',
    filterStatus: 'active' as const,
  },
  offline: {
    wrapperBg: 'rgba(248, 113, 113, 0.08)',
    wrapperBorder: '1px solid rgba(248, 113, 113, 0.25)',
    badgeBg: 'rgba(248, 113, 113, 0.2)',
    badgeText: '#fca5a5',
    btnBg: 'rgba(248, 113, 113, 0.12)',
    btnBorder: '1px solid rgba(248, 113, 113, 0.25)',
    btnSelectedBg: 'rgba(248, 113, 113, 0.28)',
    btnSelectedBorder: '2px solid rgba(248, 113, 113, 0.8)',
    selectedText: '#fca5a5',
    filterStatus: 'offline' as const,
  },
};

export function PlatformBreakdown({
  title,
  totalCount,
  items,
  variant,
  statusFilter,
  platformFilter,
  onPlatformClick,
}: PlatformBreakdownProps) {
  const theme = THEME[variant];

  return (
    <div
      className="rounded-2xl px-6 py-5"
      style={{ backgroundColor: theme.wrapperBg, border: theme.wrapperBorder }}
    >
      <div className="flex items-center justify-between">
        <div className="text-base font-semibold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          {title}
        </div>
        <div
          className="text-sm font-semibold px-3 py-1 rounded-full"
          style={{ backgroundColor: theme.badgeBg, color: theme.badgeText }}
        >
          {totalCount} total {variant}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-4">
        {items.map((item) => {
          const isSelected = statusFilter === theme.filterStatus && platformFilter === item.platformKey;
          return (
            <button
              key={item.key}
              onClick={() => onPlatformClick(item.platformKey)}
              className="rounded-xl px-4 py-4 text-left w-full transition-all duration-150 hover:scale-[1.03]"
              style={{
                backgroundColor: isSelected ? theme.btnSelectedBg : theme.btnBg,
                border: isSelected ? theme.btnSelectedBorder : theme.btnBorder,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <div className="text-sm" style={{ color: isSelected ? theme.selectedText : 'rgba(255, 255, 255, 0.75)' }}>
                {item.label}
              </div>
              <div className="text-3xl font-semibold" style={{ color: '#ffffff' }}>
                {item.count}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
